import { isNullOrUndefined, loadJS, createLoaded } from './utils';

/**
 * 神策统计包装类
 */
const Log = class {
  constructor($config, $ctx) {
    this.$config = $config;
    this.$ctx = $ctx;
    // 初始化通用参数
    this.commonParams = {};
    // 发送神策统计前钩子
    this.beforeSendStat = null;
    // 自动发页面pv统计的方法
    this.autoSendPV = null;
    // 日志服务集合
    this.logersLoaded = [];
    if (this.$config.log?.saSDK) {
      // 默认添加神策统计SDK
      this.logersLoaded.push(createLoaded(() => $ctx.onload.then(() => loadJS(this.$config.log?.saSDK, 1e4).then(() => {
        const sensors = window.sensorsDataAnalytic201505;
        if (sensors) {
          sensors.init({
            server_url: this.$config.log?.saUrl,
            show_log: false,
            // send_type: 'beacon',
          });
          return {
            login(userId) {
              sensors.login(userId);
            },
            track(type, params) {
              sensors.track(type, params);
            },
          };
        }
        console.warn('sensors init error');
        return null;
      })), false));
    }

    // 事件类型（简化去掉了Trigger、Click类型，由tag替代)
    this.EVENT_TYPE = {
      CLICK: 'page_click', // 事件埋点
      VIEW: 'page_view', // 页面PV埋点(兼容旧版埋点)
      SHOW: 'page_show', // 曝光/展示
      INPUT: 'page_input', // 输入事件
      TRIGGER: 'page_trigger', // 触发事件
      // HOTMAP: 'page_hotmap', // 热点地图埋点
      ERROR: 'hobbit_error', // 报错埋点
      API: 'hobbit_api', // 端到端监控统计
    };

    // 异常级别
    this.ERROR_LEVEL = {
      FATAL: 'fatal',
      ERROR: 'error',
      WARN: 'warn',
      INFO: 'info',
    };

    // 异常类型
    this.ERROR_TYPE = {
      PROMISE: 'promise',
      RESOURCE: 'resource',
      JSERROR: 'jserror',
      VUE: 'vue',
      MANUAL: 'manual',
    };

    // 日志行列分隔符（可选安全分隔符：字母、数字和字符$-_.+!*'()）
    this.LOG_SPLIT_CHAR = {
      COLUMN: '-',
      ROW: '.',
    };

    // 扩展字段key前缀
    this.extParamsKey = 'vkdata_';
    // 字母序列
    this.LETTER_SEQUENCE = 'abcdefghijklmnopqrstuvwxyz';
  }

  /**
   * 设置通用参数
   */
  setCommonParams(params) {
    this.commonParams = {
      ...this.commonParams,
      ...params,
    };
  }

  /**
   * 同步用户ID
   */
  syncUserId(userId) {
    for (const logerLoaded of this.logersLoaded) {
      logerLoaded()?.then(loger => {
        loger.login(userId);
      });
    }
  }

  /**
   * 统计上报(通用上报，如非必要，请不要直接使用该方法上报)
   */
  stat(eventType, params = {}) {
    if (typeof this.beforeSendStat === 'function') {
      this.beforeSendStat(eventType, params);
    }

    const sendParams = {
      ...this.commonParams,
      ...params,
    };

    for (const logerLoaded of this.logersLoaded) {
      logerLoaded()?.then(loger => {
        loger.track(eventType, sendParams);
      }).catch(err => {
        console.warn('logerLoaded err!', err);
      });
    }

    if (this.$config.log?.consoleLog) {
      console.log(`%c$log:${eventType}`, 'color:#39f', sendParams);
    }
  }

  /**
   * 【已淘汰】请改用click
   * 点击埋点（旧规范写法）
   */
  tag(eventId, ...params) {
    this.click(eventId, this.vkdata2Object(params));
  }

  /**
   * 点击埋点
   */
  click(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.CLICK, params);
  }

  /**
   * 页面pv，建议自动发送（params参数不可自定义）
   */
  view(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.VIEW, params);
  }

  /**
   * 曝光/展示事件
   */
  show(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.SHOW, params);
  }

  /**
   * 输入事件
   */
  input(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.INPUT, params);
  }

  /**
   * 触发事件
   */
  trigger(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.TRIGGER, params);
  }

  /**
   * 端到端监控统计
   */
  api(eventId, params = {}) {
    params.event_id = eventId;
    this.stat(this.EVENT_TYPE.API, params);
  }

  /**
   * 错误上报
   */
  error(
    error,
    type = this.ERROR_TYPE.MANUAL,
    level = this.ERROR_LEVEL.ERROR,
  ) {
    let errorInstance = null;
    if (error instanceof Error) {
      errorInstance = error;
    } else {
      if (typeof error !== 'string') {
        error = JSON.stringify(error);
      }
      errorInstance = new Error(error);
    }

    const errorMessageList = [];
    if (errorInstance.name) errorMessageList.push(errorInstance.name);
    if (errorInstance.fileName) errorMessageList.push(errorInstance.fileName);
    if (errorInstance.lineNumber) errorMessageList.push(errorInstance.lineNumber);
    if (errorInstance.columnNumber) errorMessageList.push(errorInstance.columnNumber);
    const message = errorInstance.message || errorInstance.description || String(errorInstance);
    errorMessageList.push(message);
    if (errorInstance.stack) {
      errorMessageList.push(String(errorInstance.stack)
        .replace(/\(.*\/|\s{2,}/g, '')
        .replace(/\)?at\s/g, '@'));
    }

    const errorMessage = errorMessageList.join(' ');
    this.stat(this.EVENT_TYPE.ERROR, {
      event_id: type,
      errorMessage,
      level,
    });
  }

  warn(error, type) {
    this.error(error, type, this.ERROR_LEVEL.WARN);
  }

  fatal(error, type) {
    this.error(error, type, this.ERROR_LEVEL.FATAL);
  }

  info(error, type) {
    this.error(error, type, this.ERROR_LEVEL.INFO);
  }

  /**
   * 自定义vkdata参数对象化
   */
  vkdata2Object(vkdata) {
    const params = {};
    if (vkdata && vkdata.length) {
      for (let i = 0; i < vkdata.length; i += 1) {
        params[`${this.extParamsKey}${this.LETTER_SEQUENCE.charAt(i)}`] = `${vkdata[i]}`;
      }
    }
    return params;
  }

  /**
   * 数组
   */
  array2LogString(arr) {
    return Array.isArray(arr) ? arr.join(this.LOG_SPLIT_CHAR.ROW) : '';
  }

  /**
   * 扁平对象转日志字符串
   */
  bean2LogString(obj) {
    if (isNullOrUndefined(obj)) {
      return '';
    }
    const arr = [];
    for (const key in obj) {
      arr.push(`${key}${this.LOG_SPLIT_CHAR.COLUMN}${obj[key]}`);
    }
    return this.array2LogString(arr);
  }
};

export default Log;
